/**
 * MetaMask Connection Manager
 * Handles MetaMask connection issues including "Connection declined" errors
 * and pending request conflicts
 */

interface MetaMaskProvider {
  request: (args: { method: string; params?: any[] }) => Promise<any>;
  isMetaMask?: boolean;
  selectedAddress?: string;
  chainId?: string;
  isConnected?: () => boolean;
}

interface ConnectionState {
  isConnecting: boolean;
  lastAttempt: number;
  pendingRequest: Promise<any> | null;
}

class MetaMaskConnectionManager {
  private connectionState: ConnectionState = {
    isConnecting: false,
    lastAttempt: 0,
    pendingRequest: null,
  };

  private readonly DEBOUNCE_TIME = 1000; // 1 second
  private readonly MAX_RETRY_ATTEMPTS = 3;
  private readonly RETRY_DELAY = 2000; // 2 seconds

  /**
   * Get MetaMask provider with validation
   */
  private getMetaMaskProvider(): MetaMaskProvider | null {
    if (typeof window === "undefined") return null;

    try {
      const ethereum = (window as any).ethereum;
      if (!ethereum) return null;

      // If multiple providers exist, find MetaMask specifically
      if (ethereum.providers && Array.isArray(ethereum.providers)) {
        return (
          ethereum.providers.find((provider: any) => provider.isMetaMask) ||
          null
        );
      }

      // Single provider case
      return ethereum.isMetaMask ? ethereum : null;
    } catch (error) {
      console.warn(
        "MetaMaskConnectionManager: Error accessing ethereum provider:",
        error
      );
      return null;
    }
  }

  /**
   * Clear any pending connection requests in MetaMask
   */
  private async clearPendingRequests(): Promise<void> {
    const provider = this.getMetaMaskProvider();
    if (!provider) return;

    try {
      // Try to get current accounts to check connection state
      await provider.request({ method: "eth_accounts" });
    } catch (error: any) {
      console.log(
        "MetaMaskConnectionManager: Cleared pending state check:",
        error.message
      );
    }
  }

  /**
   * Check if connection request should be debounced
   */
  private shouldDebounce(): boolean {
    const now = Date.now();
    return now - this.connectionState.lastAttempt < this.DEBOUNCE_TIME;
  }

  /**
   * Wait for any existing connection request to complete
   */
  private async waitForPendingRequest(): Promise<void> {
    if (this.connectionState.pendingRequest) {
      try {
        await this.connectionState.pendingRequest;
      } catch (error) {
        // Ignore errors from previous requests
        console.log(
          "MetaMaskConnectionManager: Previous request completed with error"
        );
      }
    }
  }

  /**
   * Attempt to connect to MetaMask with retry logic
   */
  private async attemptConnection(retryCount = 0): Promise<string[]> {
    const provider = this.getMetaMaskProvider();
    if (!provider) {
      throw new Error("MetaMask not found. Please install MetaMask extension.");
    }

    try {
      // Clear any pending requests first
      await this.clearPendingRequests();

      // Small delay to ensure MetaMask is ready
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Request account access with timeout
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(
          () => reject(new Error("Connection request timed out")),
          30000
        );
      });

      const accountsPromise = provider.request({
        method: "eth_requestAccounts",
      });

      const accounts = await Promise.race([accountsPromise, timeoutPromise]);

      if (!accounts || accounts.length === 0) {
        throw new Error("No accounts returned from MetaMask");
      }

      return accounts;
    } catch (error: any) {
      console.error(
        `MetaMaskConnectionManager: Connection attempt ${
          retryCount + 1
        } failed:`,
        error
      );

      // Handle stream disconnection errors
      if (
        error.message?.includes("stream was destroyed") ||
        error.message?.includes("Lost connection") ||
        error.message?.includes("write after a stream was destroyed")
      ) {
        console.warn(
          "MetaMaskConnectionManager: Stream disconnection detected, retrying..."
        );
        if (retryCount < this.MAX_RETRY_ATTEMPTS) {
          await new Promise((resolve) =>
            setTimeout(resolve, this.RETRY_DELAY * 2)
          );
          return this.attemptConnection(retryCount + 1);
        } else {
          throw new Error(
            "MetaMask connection lost. Please try refreshing the page or reconnecting your wallet."
          );
        }
      }

      // Handle specific MetaMask errors
      if (error.code === 4001) {
        // User rejected the request
        throw new Error(
          "Connection request was rejected. Please try again and approve the connection in MetaMask."
        );
      }

      if (error.code === -32002) {
        // Request already pending
        if (retryCount < this.MAX_RETRY_ATTEMPTS) {
          console.log(
            `MetaMaskConnectionManager: Retrying connection in ${this.RETRY_DELAY}ms...`
          );
          await new Promise((resolve) => setTimeout(resolve, this.RETRY_DELAY));
          return this.attemptConnection(retryCount + 1);
        } else {
          throw new Error(
            "Connection request is already pending. Please check MetaMask and try again, or restart your browser if the issue persists."
          );
        }
      }

      // Handle connection timeout
      if (error.message?.includes("timed out")) {
        throw new Error(
          "Connection request timed out. Please ensure MetaMask is responding and try again."
        );
      }

      // For other errors, retry if we haven't exceeded max attempts
      if (retryCount < this.MAX_RETRY_ATTEMPTS) {
        console.log(
          `MetaMaskConnectionManager: Retrying connection in ${this.RETRY_DELAY}ms...`
        );
        await new Promise((resolve) => setTimeout(resolve, this.RETRY_DELAY));
        return this.attemptConnection(retryCount + 1);
      }

      throw error;
    }
  }

  /**
   * Connect to MetaMask with comprehensive error handling
   */
  public async connect(): Promise<string[]> {
    // Check if already connecting
    if (this.connectionState.isConnecting) {
      console.log(
        "MetaMaskConnectionManager: Connection already in progress, waiting..."
      );
      await this.waitForPendingRequest();
      return [];
    }

    // Debounce rapid connection attempts
    if (this.shouldDebounce()) {
      console.log("MetaMaskConnectionManager: Connection request debounced");
      throw new Error("Please wait a moment before trying to connect again.");
    }

    // Update connection state
    this.connectionState.isConnecting = true;
    this.connectionState.lastAttempt = Date.now();

    try {
      // Create and store the connection promise
      const connectionPromise = this.attemptConnection();
      this.connectionState.pendingRequest = connectionPromise;

      const accounts = await connectionPromise;

      console.log(
        "MetaMaskConnectionManager: Successfully connected to MetaMask"
      );
      return accounts;
    } catch (error: any) {
      console.error("MetaMaskConnectionManager: Connection failed:", error);
      throw error;
    } finally {
      // Reset connection state
      this.connectionState.isConnecting = false;
      this.connectionState.pendingRequest = null;
    }
  }

  /**
   * Check if MetaMask is already connected
   */
  public async isConnected(): Promise<boolean> {
    const provider = this.getMetaMaskProvider();
    if (!provider) return false;

    try {
      const accounts = await provider.request({ method: "eth_accounts" });
      return accounts && accounts.length > 0;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get current accounts without triggering connection
   */
  public async getAccounts(): Promise<string[]> {
    const provider = this.getMetaMaskProvider();
    if (!provider) return [];

    try {
      const accounts = await provider.request({ method: "eth_accounts" });
      return accounts || [];
    } catch (error) {
      console.error(
        "MetaMaskConnectionManager: Failed to get accounts:",
        error
      );
      return [];
    }
  }

  /**
   * Reset connection manager state
   */
  public reset(): void {
    this.connectionState = {
      isConnecting: false,
      lastAttempt: 0,
      pendingRequest: null,
    };
  }

  /**
   * Get connection state for debugging
   */
  public getConnectionState(): ConnectionState {
    return { ...this.connectionState };
  }
}

// Create singleton instance
const metaMaskConnectionManager = new MetaMaskConnectionManager();

export default metaMaskConnectionManager;

// Export utility functions
export const connectMetaMask = () => metaMaskConnectionManager.connect();
export const isMetaMaskConnected = () =>
  metaMaskConnectionManager.isConnected();
export const getMetaMaskAccounts = () =>
  metaMaskConnectionManager.getAccounts();
export const resetMetaMaskConnection = () => metaMaskConnectionManager.reset();
